export interface TeamMember {
  name: string
  role: string
  bio: string
  image?: string
  linkedin?: string
}

export interface CompanyFact {
  value: string
  label: string
}

export interface CompanyValue {
  title: string
  description: string
}

export interface JobOpening {
  title: string
  department: string
  location: string
  type: 'Full-time' | 'Part-time' | 'Contract'
}

export interface Partner {
  name: string
  description: string
  logo?: string
}

export const companyInfo = {
  name: 'Templatean',
  tagline: 'Documents that work as hard as you do',
  mission: 'We believe every professional deserves tools that amplify their expertise, not slow them down. Templatean exists to eliminate the busywork that keeps talented people from doing their best work.',
  vision: 'A world where creating perfect documents is effortless—where AI handles the tedious parts so humans can focus on strategy, creativity, and relationships.',
  story: `Templatean was born from a simple frustration: why do smart, capable professionals spend so much time wrestling with documents?

We saw it firsthand—consultants spending more time formatting proposals than advising clients. Legal teams copying and pasting clauses for the hundredth time. Finance leaders checking formulas instead of analyzing strategy.

In 2026, we set out to change that. We're building document tools that actually understand how work gets done, powered by the latest advances in AI.

Our mission is to help organizations create better documents in less time. We're just getting started.`,
  founded: '2026',
  headquarters: 'London, UK',
}

export const companyFacts: CompanyFact[] = [
  { value: '2026', label: 'Founded' },
  { value: 'London, UK', label: 'Headquarters' },
]

export const companyValues: CompanyValue[] = [
  {
    title: 'Customer Obsession',
    description: 'Every decision starts with the question: "How does this help our customers do their best work?" We visit customer offices, shadow their workflows, and build what they actually need—not what we think is cool.',
  },
  {
    title: 'Radical Simplicity',
    description: 'The best software feels invisible. We fight feature bloat relentlessly and measure success by what we can remove, not what we can add. Simple beats powerful every time.',
  },
  {
    title: 'Trust Through Transparency',
    description: 'We tell customers what our product can\'t do as clearly as what it can. We share our roadmap openly. We admit mistakes quickly and fix them faster.',
  },
  {
    title: 'Move Fast, Stay Humble',
    description: 'We ship quickly and learn from real usage. We celebrate being wrong because it means we learned something. We know we don\'t have all the answers—yet.',
  },
]

export const leadershipTeam: TeamMember[] = [
  {
    name: 'Alexandra Foster',
    role: 'Chief Executive Officer',
    bio: 'Former McKinsey partner who spent 12 years helping enterprises transform their operations. Alexandra saw firsthand how document chaos derailed even the best-run organizations. She founded Templatean to fix that.',
    linkedin: 'https://linkedin.com/in/alexandra-foster',
  },
  {
    name: 'Dr. Michael Chen',
    role: 'Chief Technology Officer',
    bio: 'Previously led AI research at Google DeepMind, where he pioneered enterprise applications of large language models. Michael holds a PhD in Machine Learning from MIT and has 15+ patents in AI-powered document understanding.',
    linkedin: 'https://linkedin.com/in/michael-chen-ai',
  },
  {
    name: 'Sarah Williams',
    role: 'Chief Product Officer',
    bio: 'Built product at Figma during its hypergrowth phase, then led product at Notion. Sarah obsesses over the details that make software feel magical. She believes enterprise tools should be as delightful as consumer apps.',
    linkedin: 'https://linkedin.com/in/sarah-williams-product',
  },
  {
    name: 'James Okonkwo',
    role: 'Chief Revenue Officer',
    bio: 'Scaled sales at Salesforce and Slack from early stage to IPO. James builds go-to-market teams that customers actually want to hear from. He\'s helped 100+ enterprise companies adopt new technologies.',
    linkedin: 'https://linkedin.com/in/james-okonkwo',
  },
  {
    name: 'Emma Richardson',
    role: 'VP of Engineering',
    bio: 'Engineering leader who built and scaled platform teams at Stripe. Emma architects systems that handle millions of documents while maintaining the performance users expect. She\'s passionate about building inclusive engineering cultures.',
    linkedin: 'https://linkedin.com/in/emma-richardson-eng',
  },
  {
    name: 'David Nakamura',
    role: 'VP of Customer Success',
    bio: 'Built customer success functions at Zendesk and HubSpot. David invented our "Time to Value" methodology that helps customers see results in their first week. He believes the best support is proactive, not reactive.',
    linkedin: 'https://linkedin.com/in/david-nakamura-cs',
  },
]

export const jobOpenings: JobOpening[] = [
  { title: 'Senior AI/ML Engineer', department: 'Engineering', location: 'London, UK (Hybrid)', type: 'Full-time' },
  { title: 'Staff Frontend Engineer', department: 'Engineering', location: 'Remote (Europe)', type: 'Full-time' },
  { title: 'Platform Engineer', department: 'Engineering', location: 'London, UK (Hybrid)', type: 'Full-time' },
  { title: 'Product Designer', department: 'Product', location: 'Remote (Global)', type: 'Full-time' },
  { title: 'Enterprise Account Executive', department: 'Sales', location: 'New York, US', type: 'Full-time' },
  { title: 'Customer Success Manager', department: 'Customer Success', location: 'London, UK', type: 'Full-time' },
  { title: 'Technical Writer', department: 'Product', location: 'Remote (Global)', type: 'Full-time' },
  { title: 'Security Engineer', department: 'Engineering', location: 'London, UK (Hybrid)', type: 'Full-time' },
]

export const partners: Partner[] = [
  {
    name: 'Microsoft',
    description: 'Featured Microsoft 365 partner with deep integration across Word, Excel, PowerPoint, and Outlook.',
  },
]

export const contactInfo = {
  email: 'hello@templatean.com',
  sales: 'sales@templatean.com',
  support: 'support@templatean.com',
  press: 'press@templatean.com',
  address: {
    street: '71-75 Shelton Street',
    city: 'London',
    region: 'Greater London',
    postalCode: 'WC2H 9JQ',
    country: 'United Kingdom',
  },
  /** Company accounts shown in the footer. Leave one empty to hide it. */
  social: {
    linkedin: 'https://linkedin.com/company/templatean',
    x: 'https://x.com/templatean',
    youtube: '',
    facebook: '',
    github: 'https://github.com/templatean',
  },
}

/* ----------------------------------------------------------------------------
   Derived content for the editorial About page. Nothing new is claimed here:
   the milestones are the four paragraphs of `companyInfo.story`, and the
   badges are the apps and compliance claims already made on the home page.
---------------------------------------------------------------------------- */

export interface StoryMilestone {
  /** Big display marker on the timeline tile (a year where the story has one). */
  marker: string
  title: string
  text: string
}

const storyParagraphs = companyInfo.story.split('\n\n')

export const storyMilestones: StoryMilestone[] = [
  { marker: 'Why?', title: 'The frustration', text: storyParagraphs[0] ?? '' },
  { marker: 'Who?', title: 'Seen firsthand', text: storyParagraphs[1] ?? '' },
  { marker: companyInfo.founded, title: 'Founded', text: storyParagraphs[2] ?? '' },
  { marker: 'Now.', title: 'Just getting started', text: storyParagraphs[3] ?? '' },
]

export type PlatformBadgeKind = 'word' | 'excel' | 'powerpoint' | 'outlook' | 'suite' | 'files' | 'security' | 'health' | 'privacy' | 'identity'

export interface PlatformBadge {
  label: string
  kind: PlatformBadgeKind
}

/** Where Templatean runs and the compliance it claims (from the home page). */
export const platformBadges: PlatformBadge[] = [
  { label: 'Word', kind: 'word' },
  { label: 'Excel', kind: 'excel' },
  { label: 'PowerPoint', kind: 'powerpoint' },
  { label: 'Outlook', kind: 'outlook' },
  { label: 'Microsoft 365', kind: 'suite' },
  { label: 'SharePoint', kind: 'files' },
  { label: 'SOC 2 Type II', kind: 'security' },
  { label: 'HIPAA Ready', kind: 'health' },
  { label: 'GDPR Compliant', kind: 'privacy' },
  { label: 'Enterprise SSO', kind: 'identity' },
]

/** The contract-review pipeline shown in the workflow-builder screenshot. */
export const walkthroughSteps: string[] = [
  'Contract uploaded',
  'Clara extracts terms',
  'Clara identifies risks',
  'Justice checks compliance',
  'Risk level check',
  'Atticus drafts response memo',
]
